import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  addDoc,
  serverTimestamp,
  query,
  onSnapshot,
  orderBy,
  arrayUnion,
  updateDoc
} from "firebase/firestore";
import axios from "axios";
import { db } from "@/lib/firebase";
import { notificationCount } from "./partInquirySlice";

const baseURL = import.meta.env.VITE_API_BASE_URL;

export const submitPartInquiry = createAsyncThunk(
  "partInquiry/submit",
  async ({ formData, buyerId, role }, { rejectWithValue }) => {
    try {
      const inquiryDocRef = doc(db, "Inquiries", buyerId);
      const inquiryDocSnap = await getDoc(inquiryDocRef);

      if (!inquiryDocSnap.exists()) {
        await setDoc(inquiryDocRef, {
          buyerId: buyerId,
          createdAt: serverTimestamp()
        });
      } else {
        await setDoc(
          inquiryDocRef,
          {
            updatedAt: serverTimestamp()
          },
          { merge: true }
        );
      }

      const inquiriesSubCollectionRef = collection(
        db,
        "Inquiries",
        buyerId,
        "InquiriesDetail"
      );

      const newInquiryDoc = await addDoc(inquiriesSubCollectionRef, {
        ...formData,
        submittedAt: serverTimestamp(),
        isRead: [],
        bell: false
      });

      await notifyPremiumSellers(formData, buyerId, role);

      return {
        success: true,
        inquiryId: newInquiryDoc.id,
        message: "Inquiry submitted successfully"
      };
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      return rejectWithValue(error.message);
    }
  }
);

const notifyPremiumSellers = async (formData, buyerId, role) => {
  try {
    const subscriptionsRef = collection(db, "subscriptions");
    const subsSnapshot = await getDocs(subscriptionsRef);

    const premiumUserIds = [];
    subsSnapshot.forEach((docSnap) => {
      const subData = docSnap.data();
      if (
        subData.previousPlanId === "pro" ||
        subData.previousPlanId === "premium" ||
        subData.planId === "pro" ||
        subData.planId === "premium"
      ) {
        if (!(role === "seller" && subData.userId === buyerId)) {
          premiumUserIds.push(subData.userId);
        }
      }
    });

    if (premiumUserIds.length === 0) {
      console.log("No premium or pro sellers found.");
      return;
    }

    const emailArray = [];
    for (const uid of premiumUserIds) {
      const userRef = doc(db, "users", uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        if (userData.email) {
          emailArray.push(userData.email);
        }
      }
    }

    if (emailArray.length === 0) {
      console.log("No emails found for premium/pro users.");
      return;
    }

    await sendInquiryEmail({
      sellerEmails: emailArray,
      buyerId,
      formData
    });
  } catch (error) {
    console.error("Error notifying premium sellers:", error);
    throw error;
  }
};

const sendInquiryEmail = async ({ sellerEmails, buyerId, formData }) => {
  try {
    const templateData = {
      partName: formData.partName,
      vehicleMake: formData.vehicleMake,
      vehicleModel: formData.vehicleModel,
      vehicleYear: formData.vehicleYear,
      postalCode: formData.postalCode,
      message: formData.message || "No additional details provided",
      chatUrl: `${import.meta.env.VITE_APP_BASE_URL}/?part=${encodeURIComponent(
        formData.partName
      )}&openChat=true&buyerId=${buyerId}`
    };

    await axios.post(`${baseURL}/email`, {
      sellerEmails,
      templateData
    });
  } catch (error) {
    console.error("Error sending inquiry email to sellers:", error);
  }
};

export const fetchBuyerInquiries = createAsyncThunk(
  "partInquiry/fetchHistory",
  async ({ buyerId }, { rejectWithValue }) => {
    try {
      const inquiriesRef = collection(db, "Inquiries", buyerId, "inquiries");
      const inquiriesSnapshot = await getDocs(inquiriesRef);

      const inquiries = inquiriesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      return inquiries;
    } catch (error) {
      console.error("Error fetching inquiries:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Real-time notifications subscription (no pagination in thunk)
export const subscribeToNotifications = (dispatch, userId) => {
  if (!userId) return () => {};

  const userRef = doc(db, "users", userId);
  getDoc(userRef)
    .then((userSnap) => {
      const userData = userSnap.data();
      const userCreatedAt = userData?.createdAt || new Date(0);
      const inquiriesCollectionRef = collection(db, "Inquiries");

      const unsubscribe = onSnapshot(
        inquiriesCollectionRef,
        async (inquiriesSnapshot) => {
          try {
            const allNotifications = [];

            for (const inquiryDoc of inquiriesSnapshot.docs) {
              const buyerId = inquiryDoc.id;
              const buyerRef = doc(db, "users", buyerId);
              const buyerSnap = await getDoc(buyerRef);

              let buyerDetail = {
                buyerId,
                buyerName: "Unknown Buyer",
                buyerEmail: "N/A",
                buyerPhone: "N/A"
              };

              if (buyerSnap.exists()) {
                const userData = buyerSnap.data();
                buyerDetail = {
                  buyerId,
                  buyerName:
                    userData.name || userData.displayName || "Unknown Buyer",
                  buyerEmail: userData.email || "N/A",
                  buyerPhone: userData.phone || "N/A",
                  buyerPhoto: userData.photoURL || null
                };
              }
              const inquiriesDetailRef = collection(
                db,
                "Inquiries",
                buyerId,
                "InquiriesDetail"
              );
              const inquiriesDetailQuery = query(
                inquiriesDetailRef,
                orderBy("submittedAt", "desc")
              );
              const inquiriesDetailSnap = await getDocs(inquiriesDetailQuery);
              const userCreatedAtDate =
                userCreatedAt?.toDate?.() ||
                new Date(userCreatedAt) ||
                new Date(0);

              const allInquiries = inquiriesDetailSnap.docs
                .map((detailDoc) => {
                  const data = detailDoc.data();
                  const submittedAt =
                    data.submittedAt?.toDate?.() || new Date();

                  return {
                    inquiryId: detailDoc.id,
                    ...data,
                    submittedAt
                  };
                })
                .filter((inquiry) => {
                  const isAfterUserCreatedAt =
                    inquiry.submittedAt.getTime() > userCreatedAtDate.getTime();
                  return isAfterUserCreatedAt;
                });
              if (allInquiries.length > 0) {
                allNotifications.push({ buyerDetail, allInquiries });
              }
            }
            dispatch({
              type: "partInquiry/setNotifications",
              payload: allNotifications
            });
          } catch (error) {
            console.error("Error in notifications subscription:", error);
            dispatch({
              type: "partInquiry/setNotificationsError",
              payload: error.message
            });
          }
        },
        (error) => {
          console.error("Snapshot error:", error);
          dispatch({
            type: "partInquiry/setNotificationsError",
            payload: error.message
          });
        }
      );

      return unsubscribe;
    })
    .catch((error) => {
      console.error("Error fetching user:", error);
      dispatch({
        type: "partInquiry/setNotificationsError",
        payload: error.message
      });
    });
};

// Real-time unread count subscription
export const subscribeToUnreadCount = (userId, dispatch) => {
  if (!userId) return () => {};

  const userRef = doc(db, "users", userId);

  getDoc(userRef)
    .then((userSnap) => {
      const userData = userSnap.data();
      const userCreatedAtDate =
        userData?.createdAt?.toDate?.() ||
        new Date(userData?.createdAt) ||
        new Date(0);

      const inquiriesCollectionRef = collection(db, "Inquiries");

      const unsubscribe = onSnapshot(
        inquiriesCollectionRef,
        async (inquiriesSnapshot) => {
          try {
            let unreadCount = 0;

            for (const inquiryDoc of inquiriesSnapshot.docs) {
              const buyerId = inquiryDoc.id;

              const inquiriesDetailRef = collection(
                db,
                "Inquiries",
                buyerId,
                "InquiriesDetail"
              );

              const inquiriesDetailSnap = await getDocs(inquiriesDetailRef);

              inquiriesDetailSnap.docs.forEach((detailDoc) => {
                const data = detailDoc.data();

                const submittedAt =
                  data.submittedAt?.toDate?.() ||
                  new Date(data.submittedAt) ||
                  new Date();

                if (submittedAt.getTime() > userCreatedAtDate.getTime()) {
                  const isReadArray = data.isRead || [];
                  if (!isReadArray.includes(userId)) {
                    unreadCount++;
                  }
                }
              });
            }

            dispatch({
              type: "partInquiry/setUnreadCount",
              payload: unreadCount
            });
          } catch (error) {
            console.error("Error in unread count subscription:", error);
          }
        },
        (error) => {
          console.error("Unread count snapshot error:", error);
        }
      );

      return unsubscribe;
    })
    .catch((error) => {
      console.error("Error fetching user:", error);
    });
};

// Mark single inquiry as read
export const markInquiryAsRead = createAsyncThunk(
  "partInquiry/markAsRead",
  async ({ buyerId, inquiryId, userId }, { rejectWithValue }) => {
    try {
      const inquiryDocRef = doc(
        db,
        "Inquiries",
        buyerId,
        "InquiriesDetail",
        inquiryId
      );

      await updateDoc(inquiryDocRef, {
        isRead: arrayUnion(userId)
      });

      return { buyerId, inquiryId, userId };
    } catch (error) {
      console.error("Error marking inquiry as read:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Mark all inquiries as read for a user
export const markAllInquiriesAsRead = createAsyncThunk(
  "partInquiry/markAllAsRead",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const inquiriesCollectionRef = collection(db, "Inquiries");
      const inquiriesSnapshot = await getDocs(inquiriesCollectionRef);

      const updatePromises = [];

      for (const inquiryDoc of inquiriesSnapshot.docs) {
        const buyerId = inquiryDoc.id;

        const inquiriesDetailRef = collection(
          db,
          "Inquiries",
          buyerId,
          "InquiriesDetail"
        );

        const inquiriesDetailSnap = await getDocs(inquiriesDetailRef);

        inquiriesDetailSnap.docs.forEach((detailDoc) => {
          const data = detailDoc.data();
          const isReadArray = data.isRead || [];

          if (!isReadArray.includes(userId)) {
            const docRef = doc(
              db,
              "Inquiries",
              buyerId,
              "InquiriesDetail",
              detailDoc.id
            );
            updatePromises.push(
              updateDoc(docRef, {
                isRead: arrayUnion(userId)
              })
            );
          }
        });
      }

      await Promise.all(updatePromises);

      return { userId, updatedCount: updatePromises.length };
    } catch (error) {
      console.error("Error marking all as read:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateInquiryBells = createAsyncThunk(
  "inquiries/updateInquiryBells",
  async (buyerId, { rejectWithValue }) => {
    try {
      const inquiriesSubCollectionRef = collection(
        db,
        "Inquiries",
        buyerId,
        "InquiriesDetail"
      );

      const snapshot = await getDocs(inquiriesSubCollectionRef);

      if (snapshot.empty) {
        console.log("No inquiries found for this buyer.");
        return { updated: 0 };
      }

      let updatedCount = 0;

      for (const inquiryDoc of snapshot.docs) {
        const data = inquiryDoc.data();
        const inquiryRef = doc(
          db,
          "Inquiries",
          buyerId,
          "InquiriesDetail",
          inquiryDoc.id
        );

        if (data.bell !== true) {
          await updateDoc(inquiryRef, { bell: true });
          updatedCount++;
        }
      }

      return { updated: updatedCount };
    } catch (error) {
      console.error("❌ Error updating inquiry bells:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const notificationUnreadCount = (userId, dispatch) => {
  if (!userId) return () => {};

  const inquiriesCollectionRef = collection(db, "Inquiries");

  const unsubscribe = onSnapshot(
    inquiriesCollectionRef,
    async (inquiriesSnapshot) => {
      try {
        let unreadCount = 0;

        const userInquiryDoc = inquiriesSnapshot.docs.find(
          (doc) => doc.id === userId
        );

        if (userInquiryDoc) {
          const inquiriesDetailRef = collection(
            db,
            "Inquiries",
            userId,
            "InquiriesDetail"
          );

          const inquiriesDetailSnap = await getDocs(inquiriesDetailRef);

          inquiriesDetailSnap.docs.forEach((detailDoc) => {
            const data = detailDoc.data();
            const isReadArray = data.isRead || [];
            const bell = data.bell || false;

            if (!isReadArray.includes(userId) && !bell) {
              unreadCount++;
            }
          });
        }

        dispatch({
          type: "partInquiry/setNotificationCount",
          payload: unreadCount
        });
      } catch (error) {
        console.error("Error in unread count subscription:", error);
      }
    },
    (error) => {
      console.error("Unread count snapshot error:", error);
    }
  );

  return unsubscribe;
};
